import React, { useState, useEffect, memo } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addDays,
  isWithinInterval,
  isSameDay,
} from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Users,
  Clock,
  Calendar as CalendarIcon,
  Filter,
  Search,
  Receipt,
  DollarSign,
} from "lucide-react";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { StaffAttendance, AttendanceType } from "@/models/attendanceModel";
import { StoreEmployee } from "@/models/storeModel";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/currencyFormatter";
import { useOrderStore } from "@/store/orderStore";
import { formatCurrency } from "@/lib/currencyFormatter";

export const EmployeeActivitiesTab = memo(
  ({
    employees,
    fetchEmployees,
    storeId,
  }: {
    employees: StoreEmployee[];
    fetchEmployees: (storeId: string) => Promise<StoreEmployee[]>;
    storeId: string;
  }) => {
    const [attendanceRecords, setAttendanceRecords] = useState<
      StaffAttendance[]
    >([]);
    const [absenceRecords, setAbsenceRecords] = useState<StaffAttendance[]>([]);
    const [activeEmployees, setActiveEmployees] = useState<StoreEmployee[]>([]);
    const [loading, setLoading] = useState(true);

    // Enhanced filtering options
    const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: endOfWeek(new Date(), { weekStartsOn: 1 }),
    });
    const [timeframeFilter, setTimeframeFilter] = useState<
      "week" | "month" | "year" | "custom"
    >("week");
    const [employeeFilter, setEmployeeFilter] = useState<string>("all");
    const [statusFilter, setStatusFilter] = useState<
      "all" | "active" | "absent" | "sick"
    >("all");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    // Set the date range based on the timeframe selection
    useEffect(() => {
      const now = new Date();
      let from: Date, to: Date;

      switch (timeframeFilter) {
        case "week":
          from = startOfWeek(now, { weekStartsOn: 1 }); // Start on Monday
          to = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case "month":
          from = startOfMonth(now);
          to = endOfMonth(now);
          break;
        case "year":
          from = startOfYear(now);
          to = endOfYear(now);
          break;
        case "custom":
          // Keep existing range for custom
          return;
        default:
          from = startOfWeek(now, { weekStartsOn: 1 });
          to = endOfWeek(now, { weekStartsOn: 1 });
      }

      setDateRange({ from, to });
    }, [timeframeFilter]);

    // Fetch attendance data based on filters
    useEffect(() => {
      const loadData = async () => {
        setLoading(true);
        try {
          // Fetch all employees first
          const allEmployees = await fetchEmployees(storeId);

          // Fetch attendance records based on the date range
          const { data: attendanceData, error: attendanceError } =
            await supabase
              .from("staff_attendance")
              .select("*")
              .gte("clock_in", dateRange.from.toISOString())
              .lte("clock_in", dateRange.to.toISOString());

          if (attendanceError) {
            console.error("Error fetching attendance:", attendanceError);
            throw attendanceError;
          }

          // Process attendance records
          const attendance: StaffAttendance[] = attendanceData.map(
            (record: any) => ({
              ...record,
              clock_in: record.clock_in ? new Date(record.clock_in) : null,
              clock_out: record.clock_out ? new Date(record.clock_out) : null,
              created_at: new Date(record.created_at),
              updated_at: new Date(record.updated_at),
            })
          );

          // Filter for absences (holiday, sick, other) and regular work
          const absences = attendance.filter(
            (a) =>
              a.type === "holiday" || a.type === "sick" || a.type === "other"
          );
          setAbsenceRecords(absences);

          const workRecords = attendance.filter(
            (a) => a.type === "work" || a.type === "overtime"
          );
          setAttendanceRecords(workRecords);

          // Calculate active employees based on work records
          const todayDate = new Date();
          const activeEmployeeIds = workRecords
            .filter(
              (record) =>
                isSameDay(record.clock_in!, todayDate) &&
                record.clock_out === null
            )
            .map((record) => record.staff_id);

          const active = allEmployees.filter((employee) =>
            activeEmployeeIds.includes(employee.id)
          );
          setActiveEmployees(active);
        } catch (err) {
          console.error("Error loading employee activity data:", err);
        } finally {
          setLoading(false);
        }
      };

      loadData();
    }, [fetchEmployees, dateRange, statusFilter]);

    // Apply filters to the data
    const filteredEmployees = employees.filter((employee) => {
      // Apply search filter
      if (
        searchQuery &&
        !`${employee.firstName} ${employee.lastName}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ) {
        return false;
      }
      return true;
    });

    const filteredAttendanceRecords = attendanceRecords.filter((record) => {
      // Apply employee filter
      if (employeeFilter !== "all" && record.staff_id !== employeeFilter) {
        return false;
      }

      // Apply status filter
      if (statusFilter === "active" && record.clock_out !== null) {
        return false;
      }

      // Apply search filter
      if (searchQuery) {
        const employee = employees.find((emp) => emp.id === record.staff_id);
        if (
          !employee ||
          !`${employee.firstName} ${employee.lastName}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        ) {
          return false;
        }
      }

      return true;
    });

    const filteredAbsenceRecords = absenceRecords.filter((record) => {
      // Apply employee filter
      if (employeeFilter !== "all" && record.staff_id !== employeeFilter) {
        return false;
      }

      // Apply status filter
      if (statusFilter === "active") {
        return false;
      }
      if (statusFilter === "sick" && record.type !== "sick") {
        return false;
      }
      if (
        statusFilter === "absent" &&
        record.type !== "holiday" &&
        record.type !== "other"
      ) {
        return false;
      }

      // Apply search filter
      if (searchQuery) {
        const employee = employees.find((emp) => emp.id === record.staff_id);
        if (
          !employee ||
          !`${employee.firstName} ${employee.lastName}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        ) {
          return false;
        }
      }

      return true;
    });

    // Calculate total hours for today's employees
    const getTodayWorkHours = (staffId: string) => {
      const today = new Date();
      const staffTodayRecord = attendanceRecords.find(
        (record) =>
          record.staff_id === staffId && isSameDay(record.clock_in!, today)
      );
      return staffTodayRecord?.total_hours || 0;
    };

    // Get employee name by ID
    const getEmployeeName = (staffId: string) => {
      const employee = employees.find((emp) => emp.id === staffId);
      return employee
        ? `${employee.firstName} ${employee.lastName}`
        : "Unknown";
    };

    // Format absence duration
    const formatAbsenceDuration = (record: StaffAttendance) => {
      if (
        record.type === "sick" ||
        record.type === "holiday" ||
        record.type === "other"
      ) {
        if (!record.leave_from || !record.back_on) return "N/A";

        const startDate = format(new Date(record.leave_from), "MMM d");
        const endDate = format(new Date(record.back_on), "MMM d");

        return `${startDate} - ${endDate}`;
      }

      if (!record.clock_in || !record.clock_out) return "N/A";
      const startDate = format(record.clock_in, "MMM d");
      const endDate = format(record.clock_out, "MMM d");
      return `${startDate} - ${endDate}`;
    };

    // Get badge color based on absence type
    const getAbsenceBadgeColor = (type: AttendanceType) => {
      switch (type) {
        case "holiday":
          return "bg-blue-500 hover:bg-blue-600";
        case "sick":
          return "bg-red-500 hover:bg-red-600";
        case "other":
          return "bg-gray-500 hover:bg-gray-600";
        default:
          return "bg-slate-500 hover:bg-slate-600";
      }
    };

    // Format the date range for display
    const formatDateRange = () => {
      return `${format(dateRange.from, "MMM d, yyyy")} – ${format(
        dateRange.to,
        "MMM d, yyyy"
      )}`;
    };

    return (
      <div className="space-y-4">
        {/* Enhanced Filter Controls */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            {/* Time Range Filter */}
            <Select
              value={timeframeFilter}
              onValueChange={(value: "week" | "month" | "year" | "custom") =>
                setTimeframeFilter(value)
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom Date Range Picker */}
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal sm:w-[240px]",
                    timeframeFilter !== "custom" &&
                      "pointer-events-none opacity-70"
                  )}
                  disabled={timeframeFilter !== "custom"}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDateRange()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={{
                    from: dateRange.from,
                    to: dateRange.to,
                  }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange({ from: range.from, to: range.to });
                      setIsCalendarOpen(false);
                    }
                  }}
                  numberOfMonths={2}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            {/* Employee Filter */}
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {`${emp.firstName} ${emp.lastName}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(value: "all" | "active" | "absent" | "sick") =>
                setStatusFilter(value)
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="absent">Absence</SelectItem>
                <SelectItem value="sick">Sick</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div className="relative w-full md:max-w-xs">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        {/* Employee Activity Cards - Similar to the dashboard component */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium">Active Employees</h3>
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold mb-2">
                {loading ? "Loading..." : activeEmployees.length}
              </div>
              <div className="space-y-2">
                {activeEmployees.length > 0 ? (
                  activeEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span>{`${employee.firstName} ${employee.lastName}`}</span>
                      <Badge
                        variant="outline"
                        className="bg-green-100 text-green-800 hover:bg-green-200"
                      >
                        Active
                      </Badge>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No employees currently active
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium">Today's Work Hours</h3>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold mb-2">
                {loading
                  ? "Loading..."
                  : filteredAttendanceRecords.filter((r) =>
                      isSameDay(r.clock_in!, new Date())
                    ).length}
              </div>
              <div className="space-y-2">
                {filteredAttendanceRecords
                  .filter((r) => isSameDay(r.clock_in!, new Date()))
                  .slice(0, 4)
                  .map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span>{getEmployeeName(record.staff_id)}</span>
                      <span>
                        {record.total_hours
                          ? `${record.total_hours.toFixed(1)} hrs`
                          : "Clocked in"}
                      </span>
                    </div>
                  ))}
                {filteredAttendanceRecords.filter((r) =>
                  isSameDay(r.clock_in!, new Date())
                ).length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    No work hours recorded today
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium">Upcoming Absences</h3>
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold mb-2">
                {loading ? "Loading..." : filteredAbsenceRecords.length}
              </div>
              <div className="space-y-2">
                {filteredAbsenceRecords.slice(0, 4).map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span>{getEmployeeName(record.staff_id)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">
                        {formatAbsenceDuration(record)}
                      </span>
                      <Badge className={getAbsenceBadgeColor(record.type)}>
                        {record.type.charAt(0).toUpperCase() +
                          record.type.slice(1)}
                      </Badge>
                    </div>
                  </div>
                ))}
                {filteredAbsenceRecords.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    No upcoming absences
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium">Period Summary</h3>
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold mb-2">
                {loading ? "Loading..." : `${filteredEmployees.length} Staff`}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Work Records:</span>
                  <span>{filteredAttendanceRecords.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Holidays:</span>
                  <span>
                    {
                      filteredAbsenceRecords.filter((r) => r.type === "holiday")
                        .length
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Sick Leave:</span>
                  <span>
                    {
                      filteredAbsenceRecords.filter((r) => r.type === "sick")
                        .length
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Other Absences:</span>
                  <span>
                    {
                      filteredAbsenceRecords.filter((r) => r.type === "other")
                        .length
                    }
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden bg-gradient-to-br from-purple-50 to-indigo-50 hover:shadow-lg transition-all duration-300 border-2 border-purple-100 rounded-xl">
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium text-purple-900">
                  Completed Orders
                </h3>
                <Receipt className="h-4 w-4 text-purple-600" />
              </div>
              <div className="space-y-2">
                {loading ? (
                  <div className="text-2xl font-bold">Loading...</div>
                ) : (
                  (employeeFilter === "all"
                    ? filteredEmployees
                    : filteredEmployees.filter(
                        (emp) => emp.id === employeeFilter
                      )
                  ).map((employee) => {
                    const metrics = useOrderStore
                      .getState()
                      .getEmployeeMetrics(employee.id);
                    return (
                      <div
                        key={employee.id}
                        className="flex items-center justify-between text-sm rounded-lg hover:bg-purple-100/50"
                      >
                        <span className="text-purple-900">{`${employee.firstName} ${employee.lastName}`}</span>
                        <Badge
                          variant="outline"
                          className="bg-purple-100 text-purple-800 border-purple-200"
                        >
                          {metrics.completedOrders}
                        </Badge>
                      </div>
                    );
                  })
                )}
                {!loading && filteredEmployees.length === 0 && (
                  <div className="text-sm text-purple-600">
                    No employee data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden bg-gradient-to-br from-emerald-50 to-teal-50 hover:shadow-lg transition-all duration-300 border-2 border-emerald-100 rounded-xl">
            <CardContent className="pt-6">
              <div className="flex flex-row items-center justify-between pb-2">
                <h3 className="text-sm font-medium text-emerald-900">
                  Revenue Generated
                </h3>
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="space-y-2">
                {loading ? (
                  <div className="text-2xl font-bold">Loading...</div>
                ) : (
                  (employeeFilter === "all"
                    ? filteredEmployees
                    : filteredEmployees.filter(
                        (emp) => emp.id === employeeFilter
                      )
                  ).map((employee) => {
                    const metrics = useOrderStore
                      .getState()
                      .getEmployeeMetrics(employee.id);
                    return (
                      <div
                        key={employee.id}
                        className="flex items-center justify-between text-sm rounded-lg hover:bg-emerald-100/50"
                      >
                        <span className="text-emerald-900">{`${employee.firstName} ${employee.lastName}`}</span>
                        <Badge
                          variant="outline"
                          className="bg-emerald-100 text-emerald-800 border-emerald-200"
                        >
                          {formatCurrency(metrics.totalRevenue)}
                        </Badge>
                      </div>
                    );
                  })
                )}
                {!loading && filteredEmployees.length === 0 && (
                  <div className="text-sm text-emerald-600">
                    No employee data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional detailed records could be added here if needed */}
      </div>
    );
  }
);
